import 'vite/modulepreload-polyfill';
import { createApp, onMounted, ref } from 'vue';

import { MARKETING_USER_PREFERENCES } from '~/constants/rudderstack';
import * as Iterable from '~/lib/iterable/iterable';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as CustomerUtils from '~/utils/customer';
import * as ValidatorUtils from '~/utils/validator';

import { useModal } from '../composables/use-modal';
import { useViewportSize } from '../composables/use-viewport-size';
import { BaseSection } from './base-section';

/**
 * Email Signup Module
 *
 * This module displays a newsletter sign-up form.
 * The module uses Vue.js to handle the email signup functionality.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class EmailSignupModule extends BaseSection {
  constructor() {
    super('[data-section-type="email-signup-module"]');
  }

  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'EmailSignupModuleSection',
      setup() {
        const { closeModal, isModalOpen, modalStates, openModal } = useModal();
        const { isMobile } = useViewportSize();
        const emailValue = ref('');
        const emailProvided = ref(false);
        const showInvalidEmailMessage = ref(false);

        const handleSignUpClick = () => {
          const email = emailValue.value.trim();
          const placementSource = MARKETING_USER_PREFERENCES.PLACEMENT.SIGNUP_MODULE;
          const iterablePayload = Iterable.buildPayload({
            email,
            isMobile: isMobile.value,
            placementSource,
          });

          // Validate email
          if (email == '' || !ValidatorUtils.isEmail(email)) {
            showInvalidEmailMessage.value = true;

            return;
          }

          // Update User cookies state
          CustomerUtils.identifyActionEmail(email, CustomerUtils.getIdentifiedUserState());

          // Identify user
          RudderstackClient.identifyUser({
            additionalTraits: RudderstackUtils.getUserMarketingEmailTraits({
              subscriptionSource: placementSource,
              subscriptionStatus: true,
            }),
            email,
          });

          // Update user marketing preferences
          RudderstackClient.userMarketingPreference({
            messageMedium: MARKETING_USER_PREFERENCES.MEDIUM.EMAIL,
            messageType: MARKETING_USER_PREFERENCES.TYPE.ALL,
            optInStatus: true,
            placement: placementSource,
          });

          // Push data to Iterable
          Iterable.pushSignUp(iterablePayload);

          // Update vue state
          emailValue.value = '';
          emailProvided.value = true;
          showInvalidEmailMessage.value = false;
        };

        return {
          closeModal,
          emailProvided,
          emailValue,
          handleSignUpClick,
          isMobile,
          isModalOpen,
          modalStates,
          openModal,
          showInvalidEmailMessage,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new EmailSignupModule();
});
